import { AxiosError, type AxiosInstance } from 'axios';
import { useAuthStore } from '@/stores';
import { toast } from './toast';

export const blockedUserCode = {
  Permanent: 'PERMANENTLY_BLOCKED',
  Temporary: 'TEMPORARILY_BLOCKED',
} as const;

export type BlockedUserCode =
  (typeof blockedUserCode)[keyof typeof blockedUserCode];

interface ErrorResponse {
  message?: string;
  code?: string;
  block_reason?: string;
  block_expiration?: string;
  [key: string]: unknown;
}

const BLOCKED_SIGNAL = 'is blocked';

function isAccountBlocked(message: string | undefined): boolean {
  return (
    typeof message === 'string' &&
    message.toLowerCase().includes(BLOCKED_SIGNAL)
  );
}

/**
 * True when the error is a blocked-user 403 already handled by the
 * user-block interceptor (structured `code` or legacy "is blocked" body),
 * so callers must not surface another toast for it.
 */
export function isBlockedUserError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  if (error.response?.status !== 403) return false;
  const data = error.response.data as ErrorResponse | undefined;
  return (
    data?.code === blockedUserCode.Permanent ||
    data?.code === blockedUserCode.Temporary ||
    isAccountBlocked(data?.message)
  );
}

export function setupUserBlockInterceptor(apiInstance: AxiosInstance) {
  apiInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ErrorResponse>) => {
      if (error.response?.status === 403) {
        const {
          message,
          code,
          block_reason: blockReason,
        } = error.response.data;

        if (code === blockedUserCode.Permanent) {
          // Structured permanent block: open the account-blocked modal
          // instead of the generic toast.
          const authStore = useAuthStore();
          authStore.accountBlockedReason = blockReason ?? '';
          authStore.showAccountBlockedModal = true;
          await authStore.logout();
        } else if (
          code === blockedUserCode.Temporary ||
          isAccountBlocked(message)
        ) {
          // Temporary block or legacy body (no code): keep today's behavior.
          const authStore = useAuthStore();
          toast.error({ title: 'Account Blocked', message });
          await authStore.logout();
        }
      }
      return Promise.reject(error);
    },
  );
}
